import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { copilotAPI, emailAPI } from '../api/api';
import { toast } from '../hooks/use-toast';

/**
 * CopilotPanel — the conversational agent, as a side panel available anywhere
 * in the app.
 *
 * The BM describes what happened ("je reviens d'un RDV, ils cherchent un Data
 * Engineer…") and the copilot creates the tender, ranks the vivier and drafts
 * the outreach. Reads and internal writes happen on their own; every *outbound*
 * action stops at a confirmation gate rendered here — the backend only ever
 * returns drafts, and the send goes through emailAPI once the BM clicks.
 */

const CSS = `
/* Le bouton « Signaler » de l'app occupe déjà bottom:20px/right:20px — on se
   place au-dessus de lui plutôt que par-dessus. */
.cp-fab {
  position: fixed; right: 20px; bottom: 82px; z-index: 60;
  height: 46px; padding: 0 18px; border-radius: 23px; border: none; cursor: pointer;
  display: flex; align-items: center; gap: 9px;
  background: linear-gradient(150deg, #8B7CF0, #6C47FF); color: #fff;
  font-family: inherit; font-size: 13px; font-weight: 700;
  box-shadow: 0 10px 30px -8px rgba(108,71,255,.75);
  transition: transform .15s ease;
}
.cp-fab:hover { transform: translateY(-2px); }
.cp-fab:focus-visible { outline: 2px solid #C4B5FD; outline-offset: 3px; }

.cp-scrim { position: fixed; inset: 0; z-index: 70; background: rgba(3,3,10,.5); backdrop-filter: blur(2px); }
.cp-panel {
  position: fixed; top: 0; right: 0; bottom: 0; z-index: 71;
  width: 460px; max-width: 100vw; display: flex; flex-direction: column;
  background: var(--bg, #05050F); border-left: 1px solid var(--border2, #2A2660);
  box-shadow: -30px 0 80px -30px rgba(0,0,0,.8);
  animation: cpIn .22s cubic-bezier(.2,.7,.2,1);
}
@keyframes cpIn { from { transform: translateX(24px); opacity: 0; } to { transform: none; opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .cp-panel { animation: none; } }

.cp-head { display: flex; align-items: center; gap: 10px; padding: 13px 16px; border-bottom: 1px solid var(--border, #1E1B50); }
.cp-logo { width: 28px; height: 28px; border-radius: 8px; display: grid; place-items: center; font-size: 14px;
  background: linear-gradient(150deg, #8B7CF0, #6C47FF); box-shadow: 0 4px 12px -3px rgba(108,71,255,.6); }
.cp-title { font-size: 14px; font-weight: 800; color: var(--white, #F0EEFF); }
.cp-sub { font-size: 10.5px; color: var(--muted2, #8E88B5); margin-top: 1px; }
.cp-head-actions { margin-left: auto; display: flex; align-items: center; gap: 2px; }
.cp-x { background: none; border: none; color: var(--muted2, #8E88B5); cursor: pointer; font-size: 18px; line-height: 1; padding: 5px 9px; border-radius: 7px; }
.cp-x:hover { background: var(--surface2, #10103A); color: var(--white, #F0EEFF); }
.cp-x:focus-visible { outline: 2px solid #8B7CF0; outline-offset: 1px; }

.cp-beta { font-size: 8.5px; font-weight: 800; letter-spacing: .7px; vertical-align: middle; margin-left: 5px;
  padding: 2px 6px; border-radius: 5px; background: rgba(234,179,8,.15); color: #EAB308; border: 1px solid rgba(234,179,8,.3); }
.cp-fab-beta { font-style: normal; font-size: 9px; font-weight: 800; letter-spacing: .5px; text-transform: uppercase;
  padding: 2px 6px; border-radius: 5px; background: rgba(255,255,255,.22); }
.cp-warn { color: #EAB308 !important; }
.cp-note { font-size: 10.5px; color: var(--muted, #5A5580); line-height: 1.55; margin-top: 14px; padding-top: 12px;
  border-top: 1px solid var(--border, #1E1B50); }

.cp-body { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 16px; }

.cp-empty { margin: auto 0; text-align: center; padding: 20px 8px; }
.cp-empty h4 { font-size: 15px; font-weight: 800; color: var(--white, #F0EEFF); margin-bottom: 7px; }
.cp-empty p { font-size: 12.5px; color: var(--muted2, #8E88B5); line-height: 1.6; margin-bottom: 16px; }
.cp-ex { display: block; width: 100%; text-align: left; margin-bottom: 8px; cursor: pointer;
  background: var(--surface, #0A0A2C); border: 1px solid var(--border, #1E1B50); border-radius: 11px;
  padding: 11px 13px; color: var(--muted2, #8E88B5); font-family: inherit; font-size: 12px; line-height: 1.5; }
.cp-ex:hover { border-color: #6C47FF; color: var(--white, #F0EEFF); }

.cp-msg { display: flex; gap: 10px; }
.cp-av { width: 26px; height: 26px; border-radius: 8px; flex: none; display: grid; place-items: center; font-size: 11px; font-weight: 700; }
.cp-av-ai { background: linear-gradient(150deg, #8B7CF0, #6C47FF); color: #fff; }
.cp-av-me { background: var(--surface2, #10103A); border: 1px solid var(--border2, #2A2660); color: var(--muted2, #8E88B5); }
.cp-content { flex: 1; min-width: 0; }
.cp-me { background: rgba(108,71,255,.13); border: 1px solid rgba(108,71,255,.28); border-radius: 4px 12px 12px 12px;
  padding: 10px 13px; font-size: 13px; color: #E4E0FF; white-space: pre-wrap; }
.cp-ai { font-size: 13px; color: var(--white, #F0EEFF); line-height: 1.6; white-space: pre-wrap; }

.cp-card { background: var(--surface, #0A0A2C); border: 1px solid var(--border, #1E1B50); border-radius: 12px; overflow: hidden; margin-top: 10px; }
.cp-card-h { display: flex; align-items: center; gap: 8px; padding: 10px 13px; border-bottom: 1px solid var(--border, #1E1B50);
  font-size: 11.5px; font-weight: 700; color: var(--muted2, #8E88B5); }
.cp-badge { margin-left: auto; font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 999px;
  background: rgba(34,197,94,.14); color: #22C55E; }

.cp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: var(--border, #1E1B50); }
.cp-cell { background: var(--surface, #0A0A2C); padding: 9px 13px; }
.cp-k { font-size: 9.5px; text-transform: uppercase; letter-spacing: .5px; color: var(--muted, #5A5580); font-weight: 700; }
.cp-v { font-size: 12.5px; font-weight: 600; color: var(--white, #F0EEFF); margin-top: 2px; }
.cp-v.warn { color: #EAB308; }
.cp-chips { display: flex; flex-wrap: wrap; gap: 5px; padding: 11px 13px; }
.cp-chip { font-size: 10.5px; font-weight: 600; padding: 4px 9px; border-radius: 7px;
  background: rgba(108,71,255,.13); color: #C4B5FD; border: 1px solid rgba(108,71,255,.22); }

.cp-cand { display: flex; align-items: center; gap: 10px; padding: 10px 13px; border-top: 1px solid var(--border, #1E1B50); }
.cp-cand:first-of-type { border-top: none; }
.cp-cav { width: 30px; height: 30px; border-radius: 9px; flex: none; display: grid; place-items: center; font-size: 11px; font-weight: 700;
  background: var(--surface2, #10103A); border: 1px solid var(--border2, #2A2660); color: var(--muted2, #8E88B5); }
.cp-cname { font-size: 12.5px; font-weight: 700; color: var(--white, #F0EEFF); }
.cp-csub { font-size: 11px; color: var(--muted2, #8E88B5); margin-top: 1px; }
.cp-score { flex: none; text-align: right; min-width: 38px; }
.cp-score b { font-size: 15px; font-weight: 800; color: #A099E8; font-variant-numeric: tabular-nums; }
.cp-score span { display: block; font-size: 8.5px; text-transform: uppercase; letter-spacing: .5px; color: var(--muted, #5A5580); font-weight: 700; }

.cp-empty-row { padding: 13px; font-size: 12px; color: var(--muted2, #8E88B5); }
.cp-tag { flex: none; font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 7px;
  background: rgba(234,179,8,.13); color: #EAB308; white-space: nowrap; }
.cp-tree { padding: 12px 13px; display: flex; flex-direction: column; gap: 7px; }
.cp-node { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--white, #F0EEFF); }
.cp-node-dot { width: 5px; height: 5px; border-radius: 50%; background: #7B68EE; flex: none; }
.cp-node em { font-style: normal; font-size: 10.5px; color: var(--muted, #5A5580); }

.cp-mail { padding: 12px 13px; }
.cp-mail-to { font-size: 11.5px; color: var(--muted2, #8E88B5); margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.cp-mail-to b { color: var(--white, #F0EEFF); }
.cp-pill { margin-left: auto; font-size: 10px; font-weight: 700; color: #C4B5FD; background: rgba(108,71,255,.14); padding: 2px 8px; border-radius: 999px; }
.cp-mail-box { background: var(--bg, #05050F); border: 1px solid var(--border, #1E1B50); border-radius: 9px; padding: 11px 12px; }
.cp-mail-subj { font-size: 12.5px; font-weight: 700; color: var(--white, #F0EEFF); margin-bottom: 6px; }
.cp-mail-body { font-size: 12px; color: var(--muted2, #8E88B5); line-height: 1.6; white-space: pre-wrap; }
.cp-nav { display: flex; align-items: center; gap: 6px; margin-top: 9px; }
.cp-nav button { width: 24px; height: 24px; border-radius: 7px; cursor: pointer; background: var(--surface2, #10103A);
  border: 1px solid var(--border2, #2A2660); color: var(--muted2, #8E88B5); font-family: inherit; }
.cp-nav button:disabled { opacity: .35; cursor: default; }

.cp-gate { display: flex; align-items: center; gap: 9px; padding: 11px 13px; border-top: 1px solid var(--border, #1E1B50); flex-wrap: wrap; }
.cp-lock { font-size: 11px; color: var(--muted2, #8E88B5); flex: 1; min-width: 150px; }
.cp-btn { font-family: inherit; font-size: 12px; font-weight: 700; border-radius: 9px; padding: 9px 14px; cursor: pointer; border: 1px solid var(--border2, #2A2660); background: var(--surface2, #10103A); color: var(--white, #F0EEFF); }
.cp-btn-primary { background: linear-gradient(150deg, #8B7CF0, #6C47FF); border-color: transparent; color: #fff; box-shadow: 0 6px 18px -6px rgba(108,71,255,.8); }
.cp-btn:disabled { opacity: .5; cursor: default; }
.cp-sent { display: flex; align-items: center; gap: 7px; padding: 11px 13px; border-top: 1px solid var(--border, #1E1B50); font-size: 11.5px; color: #22C55E; font-weight: 600; }

.cp-foot { padding: 12px 14px 14px; border-top: 1px solid var(--border, #1E1B50); }
.cp-field { display: flex; align-items: flex-end; gap: 9px; background: var(--surface, #0A0A2C); border: 1px solid var(--border2, #2A2660); border-radius: 12px; padding: 9px 11px; }
.cp-field:focus-within { border-color: rgba(108,71,255,.55); }
.cp-field textarea { flex: 1; background: none; border: none; outline: none; resize: none; color: var(--white, #F0EEFF);
  font-family: inherit; font-size: 13px; line-height: 1.5; max-height: 120px; }
.cp-field textarea::placeholder { color: var(--muted, #5A5580); }
.cp-send { width: 30px; height: 30px; border-radius: 8px; flex: none; border: none; cursor: pointer; color: #fff;
  background: linear-gradient(150deg, #8B7CF0, #6C47FF); font-size: 14px; }
.cp-send:disabled { opacity: .45; cursor: default; }
.cp-hint { text-align: center; font-size: 10px; color: var(--muted, #5A5580); margin-top: 8px; }

.cp-think { display: flex; gap: 5px; align-items: center; padding: 2px 0; }
.cp-think i { width: 5px; height: 5px; border-radius: 50%; background: #8B7CF0; animation: cpB 1.2s infinite ease-in-out; }
.cp-think i:nth-child(2) { animation-delay: .15s; }
.cp-think i:nth-child(3) { animation-delay: .3s; }
@keyframes cpB { 0%,80%,100% { opacity: .25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }

@media (max-width: 520px) { .cp-panel { width: 100vw; } .cp-grid { grid-template-columns: 1fr; } }
`;

// Un exemple par famille de tâche, pour montrer l'étendue du périmètre plutôt
// que de laisser croire que le copilot ne sait créer que des besoins.
const EXAMPLES = [
  "Je reviens d'un rendez-vous client : il recherche un Data Engineer (démarrage le 25/11, mission de 6 mois, TJM 650-700 €, 3 jours de télétravail). Crée le besoin, trouve les candidats et prépare les emails.",
  "Trouve un dev Java senior disponible en janvier.",
  "Fais-moi un point : où en est-on et qui dois-je relancer ?",
  "Crée le compte BNP Paribas, entité CIB, équipe Data & Analytics.",
];

const initials = (name) =>
  (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('fr-FR');
};

// ─── Cards ──────────────────────────────────────────────────────────────────

function BesoinCard({ card }) {
  const tjm = card.tjm_min || card.tjm_max
    ? `${card.tjm_min ?? '?'} – ${card.tjm_max ?? '?'} €`
    : null;
  const cells = [
    ['Poste', card.title, false],
    ['Client', card.client || 'À rattacher', !card.client],
    ['Démarrage', fmtDate(card.start_date), false],
    ['Durée', card.duration, false],
    ['TJM', tjm, false],
    card.remote_days != null ? ['Télétravail', `${card.remote_days} j / semaine`, false] : null,
    card.location ? ['Lieu', card.location, false] : null,
  ].filter((c) => c && c[1]);

  return (
    <div className="cp-card">
      <div className="cp-card-h">📋 Besoin créé <span className="cp-badge">Enregistré</span></div>
      <div className="cp-grid">
        {cells.map(([k, v, warn]) => (
          <div className="cp-cell" key={k}>
            <div className="cp-k">{k}</div>
            <div className={`cp-v${warn ? ' warn' : ''}`}>{v}</div>
          </div>
        ))}
      </div>
      {card.required_skills?.length > 0 && (
        <div className="cp-chips">
          {card.required_skills.map((s, i) => <span className="cp-chip" key={i}>{s.name}</span>)}
        </div>
      )}
    </div>
  );
}

function CandidatsCard({ card }) {
  if (!card.items?.length) {
    return (
      <div className="cp-card">
        <div className="cp-card-h">🎯 Candidats</div>
        <div style={{ padding: '13px', fontSize: 12, color: 'var(--muted2, #8E88B5)' }}>
          Aucun profil du vivier ne correspond encore à ce besoin.
        </div>
      </div>
    );
  }
  return (
    <div className="cp-card">
      <div className="cp-card-h">🎯 Candidats proposés
        <span className="cp-badge">{card.items.length} trouvé{card.items.length > 1 ? 's' : ''}</span>
      </div>
      {card.items.map((c) => (
        <div className="cp-cand" key={c.dossier_id}>
          <div className="cp-cav">{initials(c.nom)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cp-cname">{c.nom || 'Sans nom'}</div>
            <div className="cp-csub">
              {[c.titre, c.competences?.slice(0, 3).join(' · ')].filter(Boolean).join(' — ')}
            </div>
          </div>
          <div className="cp-score"><b>{Math.round(c.score)}</b><span>match</span></div>
        </div>
      ))}
    </div>
  );
}

function EmailsCard({ card }) {
  const [idx, setIdx] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const drafts = card.drafts || [];
  if (!drafts.length) return null;
  const d = drafts[Math.min(idx, drafts.length - 1)];
  const missing = drafts.filter((x) => !x.to).length;

  const confirmSend = async () => {
    setSending(true);
    let ok = 0;
    const failed = [];
    for (const draft of drafts) {
      if (!draft.to) { failed.push(draft.consultant_name || 'sans email'); continue; }
      try {
        await emailAPI.send({
          to_emails: [draft.to],
          subject: draft.subject,
          body_html: draft.body.replace(/\n/g, '<br>'),
          tender_id: card.besoin_id,
          dossier_ids: [draft.dossier_id],
        });
        ok += 1;
      } catch (e) {
        failed.push(draft.consultant_name || draft.to);
      }
    }
    setSending(false);
    setSent(true);
    if (ok) toast({ title: `${ok} email${ok > 1 ? 's' : ''} envoyé${ok > 1 ? 's' : ''}` });
    if (failed.length) {
      toast({
        title: `${failed.length} envoi${failed.length > 1 ? 's' : ''} en échec`,
        description: failed.join(', '),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="cp-card">
      <div className="cp-card-h">✉️ Aperçu des emails</div>
      <div className="cp-mail">
        <div className="cp-mail-to">
          À : <b>{d.consultant_name || '—'}</b>
          {!d.to && <span style={{ color: '#EAB308' }}>· email manquant</span>}
          <span className="cp-pill">Brouillon {Math.min(idx, drafts.length - 1) + 1} / {drafts.length}</span>
        </div>
        <div className="cp-mail-box">
          <div className="cp-mail-subj">{d.subject}</div>
          <div className="cp-mail-body">{d.body}</div>
        </div>
        {drafts.length > 1 && (
          <div className="cp-nav">
            <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} aria-label="Brouillon précédent">‹</button>
            <button onClick={() => setIdx((i) => Math.min(drafts.length - 1, i + 1))} disabled={idx >= drafts.length - 1} aria-label="Brouillon suivant">›</button>
          </div>
        )}
      </div>

      {sent ? (
        <div className="cp-sent">✓ Envois traités depuis ta boîte connectée.</div>
      ) : card.mailbox_connected === false ? (
        <div className="cp-gate">
          <div className="cp-lock cp-warn">
            ⚠️ Aucune boîte mail connectée. Connecte-la dans Réglages pour envoyer depuis ton adresse.
          </div>
          <button className="cp-btn" onClick={() => { window.location.assign('/settings'); }}>
            Connecter ma boîte
          </button>
        </div>
      ) : (
        <div className="cp-gate">
          <div className="cp-lock">
            🔒 Rien n'est envoyé sans ta validation.
            {missing > 0 && <> {missing} profil{missing > 1 ? 's' : ''} sans email {missing > 1 ? 'seront ignorés' : 'sera ignoré'}.</>}
          </div>
          <button className="cp-btn cp-btn-primary" onClick={confirmSend} disabled={sending || drafts.length === missing}>
            {sending ? 'Envoi…' : `Confirmer l'envoi (${drafts.length - missing})`}
          </button>
        </div>
      )}
    </div>
  );
}

/** Liste de personnes du vivier — sans score, la recherche libre n'en produit pas. */
function ConsultantsCard({ card }) {
  if (!card.items?.length) {
    return (
      <div className="cp-card">
        <div className="cp-card-h">👥 Vivier</div>
        <div className="cp-empty-row">Aucun consultant ne correspond à cette recherche.</div>
      </div>
    );
  }
  return (
    <div className="cp-card">
      <div className="cp-card-h">👥 Consultants
        <span className="cp-badge">{card.total} trouvé{card.total > 1 ? 's' : ''}</span>
      </div>
      {card.items.map((c) => (
        <div className="cp-cand" key={c.dossier_id}>
          <div className="cp-cav">{initials(c.nom)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cp-cname">{c.nom || 'Sans nom'}</div>
            <div className="cp-csub">
              {[c.titre, c.competences?.slice(0, 3).join(' · ')].filter(Boolean).join(' — ')}
            </div>
          </div>
          {c.disponibilite && <span className="cp-tag">{c.disponibilite}</span>}
        </div>
      ))}
    </div>
  );
}

function BesoinsCard({ card }) {
  if (!card.items?.length) {
    return (
      <div className="cp-card">
        <div className="cp-card-h">📋 Besoins</div>
        <div className="cp-empty-row">Aucun besoin enregistré pour l'instant.</div>
      </div>
    );
  }
  return (
    <div className="cp-card">
      <div className="cp-card-h">📋 Besoins <span className="cp-badge">{card.items.length}</span></div>
      {card.items.map((b) => (
        <div className="cp-cand" key={b.besoin_id}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cp-cname">{b.titre}</div>
            <div className="cp-csub">{[b.client || 'Client à rattacher', b.deadline && `échéance ${fmtDate(b.deadline)}`].filter(Boolean).join(' · ')}</div>
          </div>
          {b.statut && <span className="cp-tag">{b.statut}</span>}
        </div>
      ))}
    </div>
  );
}

const STAT_LABELS = {
  besoins_ouverts: 'Besoins ouverts',
  besoins_total: 'Besoins au total',
  consultants_vivier: 'Consultants',
  comptes_clients: 'Comptes clients',
  contacts_a_relancer: 'À relancer',
};

function StatsCard({ card }) {
  return (
    <div className="cp-card">
      <div className="cp-card-h">📊 Vue d'ensemble</div>
      <div className="cp-grid">
        {Object.entries(card.stats || {}).map(([k, v]) => (
          <div className="cp-cell" key={k}>
            <div className="cp-k">{STAT_LABELS[k] || k}</div>
            <div className="cp-v">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComptesCard({ card }) {
  if (!card.items?.length) {
    return (
      <div className="cp-card">
        <div className="cp-card-h">🏢 Comptes clients</div>
        <div className="cp-empty-row">Aucun compte trouvé.</div>
      </div>
    );
  }
  return (
    <div className="cp-card">
      <div className="cp-card-h">🏢 Comptes clients <span className="cp-badge">{card.items.length}</span></div>
      <div className="cp-tree">
        {card.items.map((a) => (
          <div key={a.compte_id}>
            <div className="cp-node"><span className="cp-node-dot" /><b>{a.nom}</b></div>
            {a.entites?.map((e) => (
              <div key={e.nom}>
                <div className="cp-node" style={{ marginLeft: 18 }}><span className="cp-node-dot" /><span>{e.nom}</span></div>
                {e.equipes?.map((t) => (
                  <div className="cp-node" style={{ marginLeft: 36 }} key={t}>
                    <span className="cp-node-dot" /><em>{t}</em>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function CompteCard({ card }) {
  return (
    <div className="cp-card">
      <div className="cp-card-h">🏢 Compte client
        <span className="cp-badge">{card.existant ? 'Existant' : 'Créé'}</span>
      </div>
      <div className="cp-tree">
        {card.nodes.map((n) => (
          <div className="cp-node" key={n.nom} style={{ marginLeft: n.niveau * 18 }}>
            <span className="cp-node-dot" /><b>{n.nom}</b><em>{n.type}</em>
          </div>
        ))}
      </div>
    </div>
  );
}

function RelancesCard({ card }) {
  if (!card.items?.length) {
    return (
      <div className="cp-card">
        <div className="cp-card-h">⏰ Relances</div>
        <div className="cp-empty-row">Personne à relancer — tous les contacts sont à jour.</div>
      </div>
    );
  }
  return (
    <div className="cp-card">
      <div className="cp-card-h">⏰ À relancer
        <span className="cp-badge">{card.items.length}</span>
      </div>
      {card.items.map((c) => (
        <div className="cp-cand" key={c.contact_id}>
          <div className="cp-cav">{initials(c.nom)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cp-cname">{c.nom}</div>
            <div className="cp-csub">{c.poste || '—'}</div>
          </div>
          <span className="cp-tag">{c.jours == null ? 'jamais contacté' : `${c.jours} j`}</span>
        </div>
      ))}
    </div>
  );
}

function ShortlistCard({ card }) {
  return (
    <div className="cp-card">
      <div className="cp-card-h">⭐ Ajouté à la shortlist
        <span className="cp-badge">{card.noms?.length || 0}</span>
      </div>
      <div className="cp-chips">
        {(card.noms || []).map((n) => <span className="cp-chip" key={n}>{n}</span>)}
      </div>
    </div>
  );
}

const CARD_RENDERERS = {
  besoin: BesoinCard,
  candidats: CandidatsCard,
  emails: EmailsCard,
  consultants: ConsultantsCard,
  besoins: BesoinsCard,
  stats: StatsCard,
  comptes: ComptesCard,
  compte: CompteCard,
  relances: RelancesCard,
  shortlist: ShortlistCard,
};

// ─── Panel ──────────────────────────────────────────────────────────────────

const STORE_KEY = 'sd.copilot.turns';

// The transcript survives a page change or an accidental close, but not a new
// browser session — a stale plan from yesterday is noise, not context.
const loadTurns = () => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

export default function CopilotPanel() {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState(loadTurns); // { role, content, cards? }
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [available, setAvailable] = useState(null); // null = unknown yet
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // Ask the backend whether the beta is on for this org before showing a button
  // that would only 503. A failure here hides the copilot rather than breaking
  // the page it lives on.
  useEffect(() => {
    let cancelled = false;
    copilotAPI.status()
      .then((s) => { if (!cancelled) setAvailable(!!s.enabled); })
      .catch(() => { if (!cancelled) setAvailable(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(turns)); } catch { /* quota — not worth failing over */ }
  }, [turns]);

  const reset = useCallback(() => {
    setTurns([]);
    try { sessionStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [turns, busy]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) setOpen(false);
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const send = useCallback(async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput('');
    setBusy(true);

    const next = [...turns, { role: 'user', content }];
    setTurns(next);

    try {
      // Replay only the plain transcript — cards are UI state, not context.
      const history = next.map(({ role, content: c }) => ({ role, content: c }));
      const res = await copilotAPI.chat(history);
      setTurns((t) => [...t, { role: 'assistant', content: res.reply || '', cards: res.cards || [] }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Le copilot n'a pas pu répondre.";
      setTurns((t) => [...t, { role: 'assistant', content: `⚠️ ${detail}`, cards: [] }]);
    } finally {
      setBusy(false);
    }
  }, [input, busy, turns]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Rendu dans <body> plutôt qu'en place : le shell de l'app est en
  // `position: fixed`, ce qui crée un contexte d'empilement dont aucun z-index
  // interne ne peut sortir. Sans ce portail, le bouton « Signaler » (z-40, rendu
  // à la racine) passe au-dessus du panneau et recouvre le champ de saisie.
  return createPortal(
    <>
      <style>{CSS}</style>

      {!open && available && (
        <button className="cp-fab" onClick={() => setOpen(true)} title="Copilot (⌘K)">
          <span>✦</span> Copilot <em className="cp-fab-beta">bêta</em>
        </button>
      )}

      {open && (
        <>
          <div className="cp-scrim" onClick={() => setOpen(false)} />
          <aside className="cp-panel" role="dialog" aria-label="Copilot SkillDeck">
            <div className="cp-head">
              <div className="cp-logo">✦</div>
              <div>
                <div className="cp-title">Copilot <span className="cp-beta">BÊTA</span></div>
                <div className="cp-sub">IA Mistral · Données en Europe</div>
              </div>
              <div className="cp-head-actions">
                {turns.length > 0 && (
                  <button className="cp-x" onClick={reset} title="Nouvelle conversation" aria-label="Nouvelle conversation">⟲</button>
                )}
                <button className="cp-x" onClick={() => setOpen(false)} aria-label="Fermer">×</button>
              </div>
            </div>

            <div className="cp-body" ref={bodyRef}>
              {turns.length === 0 && (
                <div className="cp-empty">
                  <h4>Raconte-moi ton rendez-vous</h4>
                  <p>Je crée le besoin, je cherche les candidats dans le vivier et je prépare les emails. Rien ne part sans ta validation.</p>
                  {EXAMPLES.map((ex, i) => (
                    <button className="cp-ex" key={i} onClick={() => send(ex)}>{ex}</button>
                  ))}
                  <p className="cp-note">
                    Fonctionnalité en bêta : relis toujours le besoin créé et les emails avant de valider.
                  </p>
                </div>
              )}

              {turns.map((t, i) => (
                <div className="cp-msg" key={i}>
                  <div className={`cp-av ${t.role === 'user' ? 'cp-av-me' : 'cp-av-ai'}`}>
                    {t.role === 'user' ? '·' : '✦'}
                  </div>
                  <div className="cp-content">
                    {t.role === 'user'
                      ? <div className="cp-me">{t.content}</div>
                      : <>
                          {t.content && <div className="cp-ai">{t.content}</div>}
                          {(t.cards || []).map((card, ci) => {
                            const R = CARD_RENDERERS[card.type];
                            return R ? <R card={card} key={ci} /> : null;
                          })}
                        </>}
                  </div>
                </div>
              ))}

              {busy && (
                <div className="cp-msg">
                  <div className="cp-av cp-av-ai">✦</div>
                  <div className="cp-content"><div className="cp-think"><i /><i /><i /></div></div>
                </div>
              )}
            </div>

            <div className="cp-foot">
              <div className="cp-field">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  placeholder="Décris ton besoin…"
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  aria-label="Message au copilot"
                />
                <button className="cp-send" onClick={() => send()} disabled={busy || !input.trim()} aria-label="Envoyer">↑</button>
              </div>
              <div className="cp-hint">Lectures automatiques · envois confirmés par toi</div>
            </div>
          </aside>
        </>
      )}
    </>,
    document.body,
  );
}
